import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Tooltip,
	IconButton,
	Alert,
	Button,
	Switch,
	Typography,
	Tabs,
	Tab,
	Divider,
} from "@mui/material";
import Datagrid from "../../components/Datagrid/Datagrid";
import ExpandableDataGrid from "../../components/ExpandableDataGrid/ExpandableDataGrid";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import OrderTracker from "../../components/OrderTracker/OrderTracker";
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import "./OrderHistory.css";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useCart } from "../../context/cartContext";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AttachmentIcon from "@mui/icons-material/Attachment";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ScheduleOrder from "../../components/ScheduleOrder/ScheduleOrder";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Swal from "sweetalert2";
import { ALERT_MESSAGES, BUTTON, LABELS, PLACEHOLDER, TABLE_HEADERS, TITLES, UI_TEXT } from "../../constants";
import { formatDate, formatDateTime } from "../../utilities/globalServiceUtils";
import { formatCurrency } from "../../utilities/currency";
import UserName from "../../components/UserName/UserName";
import { useAuth } from "../../context/authContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import { deleteOrderSchedule, getOrderSchedules, getCombinedOrdersBySite, getOrderTrackingDetails } from "../../services/orderHistoryService";
import { scheduleUpdate } from "../../services/scheduleOrderService";
import { getMachinesByIds, getSingleProduct } from "../../services/breadCrumbsService";
import DataGridSkeleton from "../../components/PageSkeletons/DataGridSkeleton";
import { toast } from "../../context/ToastContext";
import CustomChip from "../../components/CustomChip/CustomChip";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { saveRequestList } from "../../services/productCardService";
import ContactCCRForm from "../../components/ContactCCRForm/ContactCCRForm";
import { useOptions } from "../../context/optionContext";
import { ASSETS_BASE_URL } from "../../config";
import ReplacementPartNotice from "../../components/ReplacementPartNotice/ReplacementPartNotice";
import { getEffectiveProductDisplay } from "../../utilities/replacementProduct";
import { trackEvent } from "../../utilities/analyticsService";

// OrderHistory Component
const OrderHistory = () => {
	const { machineID, productID } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { selectedSite, currencySymbol, selectedSiteData, selectedSiteCurrency } = useSite();
	const { optionKeys } = useOptions();
	const { addToCart, addMultipleToCart } = useCart();
	const [orders, setOrders] = useState([]);
	const [fullOrdersData, setFullOrdersData] = useState([]);
	const [selectedOrder, setSelectedOrder] = useState();
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredSchedules, setFilteredSchedules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [productName, setProductName] = useState("");
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [selectedOrderSchedule, setSelectedOrderSchedule] = useState(null);
	const [orderSchedules, setOrderSchedules] = useState([]);
	const [tab, setTab] = useState(0);
	const [selectedScheduleRows, setSelectedScheduleRows] = useState([]);
	const [selectedScheduleViewer, setSelectedScheduleViewer] = useState(null);
	const trackingFetchedRef = useRef(new Set());
	const ordersFetchInFlightRef = useRef(false);
	const ordersLastRequestKeyRef = useRef("");
	const ordersFetchSeqRef = useRef(0);
	const lastTrackedSearchRef = useRef("");
	const [trackingLoadingOrder, setTrackingLoadingOrder] = useState(null);
	const { t } = useTranslation();
	const orderDetailsRef = useRef(null);
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	const { hasPermission, user, canViewProductPrice, canAddToCart, canAddToRequestList } = useAuth();
	const [isAuthorized, setIsAuthorized] = useState(true);
	const [buyAgainOpen, setBuyAgainOpen] = useState(false);
	const [buyAgainBusy, setBuyAgainBusy] = useState(false);
	const [buyAgainSummary, setBuyAgainSummary] = useState({
		orderNumber: "",
		purchasable: [],
		nonPurchasable: [],
		notAvailable: [],
	});
	const [ccrOpen, setCcrOpen] = useState(false);
	const [ccrPrefill, setCcrPrefill] = useState(null);
	// Bumped by "chip:navigate" so a same-site re-ask ("show my orders") retries the fetch
	// even though selectedSite/machineID/productID never actually changed. Mirrors
	// MyMachines.js's identical refreshTick pattern.
	const [refreshTick, setRefreshTick] = useState(0);
	useEffect(() => {
		const onChipNavigate = (e) => {
			if (!String(e?.detail?.path || "").startsWith("/order-history")) return;
			setRefreshTick((n) => n + 1);
		};
		window.addEventListener("chip:navigate", onChipNavigate);
		return () => window.removeEventListener("chip:navigate", onChipNavigate);
	}, []);

	const canSeeOrderHistory = !!hasPermission('order_management.view_order_history');
	const orderSchedulesEnabled = optionKeys?.enableOrderSchedules !== "false";
	const canCreateOrderSchedules =
		orderSchedulesEnabled && !!hasPermission('order_management.create_order_schedules');
	const canEditOrderSchedules =
		orderSchedulesEnabled && !!hasPermission('order_management.edit_order_schedules');
	const canDeleteOrderSchedules =
		orderSchedulesEnabled && !!hasPermission('order_management.delete_order_schedules');
	const canSeeOrderSchedules = canCreateOrderSchedules || canEditOrderSchedules || canDeleteOrderSchedules;
	const canShowOrderPrices = canViewProductPrice();
	const canShowBuyAgainCart = canAddToCart();
	const canShowBuyAgainRequestList = canAddToRequestList();
	const canShowBuyAgain = canShowBuyAgainCart || canShowBuyAgainRequestList;
	const showOrderPricesForAll = optionKeys?.showOrderHistoryPricesAll !== "false";
	const showOrderPricesForPast = showOrderPricesForAll && optionKeys?.showOrderHistoryPricesPast !== "false";
	const shouldShowPricesForOrder = useCallback((orderRow) => {
		const source = String(orderRow?.source || "").toLowerCase();
		const isPastOrder = source === "past";
		if (!canShowOrderPrices) return false;
		if (!showOrderPricesForAll) return false;
		if (isPastOrder && !showOrderPricesForPast) return false;
		return true;
	}, [canShowOrderPrices, showOrderPricesForAll, showOrderPricesForPast]);
	const isPurchasableProduct = (product) => {
		const value = product?.purchasable ?? product?.is_purchasable ?? product?.isPurchasable;
		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === 1;
		if (typeof value === "string") return ["1", "true", "yes"].includes(value.trim().toLowerCase());
		return false;
	};

	/**
	 * Group an order's lines by product+machine into buy-again buckets. Rows keep the
	 * `alternative_*` link fields so the replacement caption in the buy-again grid can open
	 * the replacement part even when it is not on the selected site.
	 *
	 * @param {Object} row Combined order row with `products[]`.
	 * @returns {Object} Buckets keyed purchasable, nonPurchasable, notAvailable.
	 */
	const buildBuyAgainSummary = (row) => {
		const products = Array.isArray(row?.products) ? row.products : [];
		const purchasableMap = new Map();
		const nonPurchasableMap = new Map();
		const notAvailable = [];

		const addToMap = (map, item, qty) => {
			const key = `${item.product_id || "unknown"}:${item.machine_id || "na"}`;
			const existing = map.get(key);
			if (existing) {
				existing.quantity += qty;
			} else {
				map.set(key, { ...item, quantity: qty, id: key });
			}
		};

		products.forEach((p, idx) => {
			const pd = p?.productsData || {};
			const product_id = pd?.product_id || p?.product_id;
			const machine_id = p?.machine_id || pd?.machine_id || row?.machine_id;
			const product_number = pd?.product_number || p?.product_number || p?.part_number || product_id;
			const product_name = pd?.product_name || p?.product_name || p?.part_name || `Product ${product_id || idx + 1}`;
			const machine_number = pd?.machine_number || p?.machine_number || row?.machine_number || "";
			const machine_name = pd?.machine_name || p?.machine_name || row?.machine_name || "";
			const quantity = Number(p?.product_quantity || p?.quantity || 1) || 1;
			const baseItem = {
				product_id,
				machine_id,
				product_number,
				product_name,
				machine_number,
				machine_name,
				alternative_item_number:
					pd?.alternative_item_number || p?.alternative_item_number,
				replacement: pd?.replacement || p?.replacement,
				alternative_product_id: pd?.alternative_product_id || p?.alternative_product_id,
				alternative_machine_id: pd?.alternative_machine_id || p?.alternative_machine_id,
				confirmed_shipping_date: p?.confirmed_shipping_date || p?.confirmed_ship_date || row?.confirmed_shipping_date,
				requested_shipping_date: p?.requested_shipping_date || row?.requested_shipping_date,
				confirmed_receipt_date: p?.confirmed_receipt_date || row?.confirmed_receipt_date,
			};

			if (!product_id || !machine_id) {
				notAvailable.push({ ...baseItem, id: `na-${idx}`, quantity, reason: "Missing part or machine reference" });
				return;
			}

			if (isPurchasableProduct(pd) || isPurchasableProduct(p)) {
				addToMap(purchasableMap, baseItem, quantity);
			} else {
				addToMap(nonPurchasableMap, baseItem, quantity);
			}
		});

		return {
			orderNumber: row?.order_number || row?.order_id || "",
			purchasable: Array.from(purchasableMap.values()),
			nonPurchasable: Array.from(nonPurchasableMap.values()),
			notAvailable,
		};
	};

	const dedupeProducts = (products = []) => {
		const map = new Map();
		(products || []).forEach((product) => {
			const key =
				product?.product_id ||
				product?.productsData?.product_id ||
				`${product?.order_id || "product"}_${map.size}`;
			if (!map.has(key)) {
				map.set(key, product);
			}
		});
		return Array.from(map.values());
	};

	const calculateOrderTotalFromProducts = (products = []) => {
		return (products || []).reduce((sum, item) => {
			const val = parseFloat(item?.product_total);
			return sum + (isNaN(val) ? 0 : val);
		}, 0);
	};

	const normalizeOrderEntry = (entry = {}) => {
		const order = entry.order || {};
		const details = entry.details || {};
		const products = dedupeProducts(entry.products || []);
		const fallbackCurrency = String(selectedSiteCurrency || "USD").toUpperCase();

		const calculatedTotal = calculateOrderTotalFromProducts(products);
		const persistedOrderTotal = Number.parseFloat(order.order_total);

		return {
			source: entry.source || order.source || "combined",
			order_id: order.order_id || order.order_number,
			order_number: order.order_number || "",
			user_display_name: order.user_display_name || "",
			site_number:
				order.site_number ||
				order.customer_site_number ||
				details.site_number ||
				entry.site?.customer_site_number ||
				"",
			order_total: Number.isFinite(persistedOrderTotal)
					? persistedOrderTotal
					: calculatedTotal,
			order_status: order.order_status || "Pending",
			order_created_date: order.order_created_date,
			order_update_date: order.order_update_date,
			order_currency: String(order.order_currency || fallbackCurrency).toUpperCase(),
			order_payment_mode: order.order_payment_mode || "",
			order_po: details.po_number || entry.order_po || "",
			billing_address: details.billing_address,
			billing_city: details.billing_city,
			billing_country: details.billing_country,
			billing_name: details.billing_name,
			billing_state: details.billing_state,
			billing_street: details.billing_street,
			billing_zip: details.billing_zip,
			delivery_terms: details.delivery_terms,
			freight_number: details.freight_number,
			mode_of_delivery: details.mode_of_delivery,
			order_notes: details.order_notes,
			po_filename: details.po_filename || entry.po_filename || "",
			po_file_url: details.po_file_url || entry.po_file_url || "",
			ship_complete: details.ship_complete,
			confirmed_shipping_date: details.confirmed_shipping_date || order.confirmed_shipping_date,
			requested_shipping_date: details.requested_shipping_date || order.requested_shipping_date,
			confirmed_receipt_date: details.confirmed_receipt_date || order.confirmed_receipt_date,
			shipping_address: details.shipping_address,
			shipping_city: details.shipping_city,
			shipping_country: details.shipping_country,
			shipping_name: details.shipping_name,
			shipping_state: details.shipping_state,
			shipping_street: details.shipping_street,
			shipping_zip: details.shipping_zip,
			products: products,
		};
	};
	const selectedSiteId = selectedSiteData?.site_id;
	const hasMachineParam = Boolean(machineID);
	const hasProductParam = Boolean(productID);

	const fetchOrderSchedules = useCallback(async () => {
		if (!selectedSiteId) {
			setOrderSchedules([]);
			return;
		}
		try {
			const response = await getOrderSchedules(selectedSiteId);
			if (response.status) {
				setOrderSchedules(response.data);
			} else {
				setOrderSchedules([]);
			}
		} catch (error) {
			console.error("Error while fetching order schedules", error);
			setOrderSchedules([]);
		}
	}, [selectedSiteId]);

	useEffect(() => {
		if (!selectedSite) {
			setLoading(false);
			return;
		}

		let mounted = true;
		const fetchSeq = ++ordersFetchSeqRef.current;

		const fetchOrders = async () => {
			const requestKey = [selectedSite, machineID || "", productID || ""].join("|");
			if (ordersFetchInFlightRef.current && ordersLastRequestKeyRef.current === requestKey) {
				return;
			}

			ordersFetchInFlightRef.current = true;
			ordersLastRequestKeyRef.current = requestKey;

			try {
				if (mounted) setLoading(true);
				setError(null);

				const req_body = { site_number: selectedSite };
				if (hasMachineParam && !hasProductParam) {
					req_body.machine_id = machineID;
					req_body.product_id = null;
					await fetchMachineName();
				} else if (hasMachineParam && hasProductParam) {
					req_body.machine_id = machineID;
					req_body.product_id = productID;
					await fetchPartName();
				}

				const response = await getCombinedOrdersBySite(req_body);
				if (!mounted || fetchSeq !== ordersFetchSeqRef.current) return;
				const rawOrders = Array.isArray(response?.data) ? response.data : [];
				const normalizedOrders = rawOrders.map(normalizeOrderEntry);

				setFullOrdersData(rawOrders);
				setOrders(normalizedOrders);
				setFilteredOrders(normalizedOrders);
				// User-reported 2026-07-22: "Show order SO-UK-007537" opened the details panel then
				// closed it again moments later, no further message sent — NOT the ?open_number=
				// URL-stripping (that's a separate, harmless address-bar cleanup). Root cause: this
				// refetch is ALSO retriggered by the same chip:navigate event (via refreshTick,
				// listener above) that the deep-link handlers use to open this exact order, and this
				// unconditional setSelectedOrder(null) ran on every fresh fetch regardless of
				// whether anything CHIP just asked to open still exists in the new data — a plain
				// keep-the-list-fresh refetch was wiping out an order the SAME turn just opened.
				// Preserve the current selection (remapped to the fresh object) when it still exists
				// in the new results; only clear it when the previously-selected order has genuinely
				// disappeared. Deliberate user actions (closing the panel, switching tabs) still call
				// setSelectedOrder(null) directly elsewhere and are unaffected by this.
				setSelectedOrder((prev) => {
					if (!prev) return null;
					return (
						normalizedOrders.find((o) =>
							(prev.order_id != null && String(o.order_id) === String(prev.order_id)) ||
							(prev.order_number && String(o.order_number) === String(prev.order_number))
						) || null
					);
				});

				if (!normalizedOrders.length) {
					setError(t('ALERT_MESSAGES.NO_ORDERS_FOR_SITE'));
				} else {
					setError(null);
				}
			} catch (error) {
				if (!mounted || fetchSeq !== ordersFetchSeqRef.current) return;
				console.error("Error while fetching the order", error);
				setOrders([]);
				setFilteredOrders([]);
				setFullOrdersData([]);
				setSelectedOrder(null);
				setError(t('ALERT_MESSAGES.ERROR_OCCURED_FETCHING_PLEASE_TRY_AGAIN'));
			} finally {
				if (fetchSeq === ordersFetchSeqRef.current) {
					ordersFetchInFlightRef.current = false;
				}
				if (mounted) setLoading(false);
			}
		};




		const fetchPartName = async () => {
			try {

				const response1 = await getSingleProduct({
					machine_id: machineID,
					product_id: productID,
					site_number: selectedSite
				});
				
				if (response1.status) {
					setProductName(response1.data[0].product_name);
				} else {
					setProductName(""); // Revert to ID if failed
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
				setProductName("");
			}
		};

		const fetchMachineName = async () => {
			try {

				const response1 = await getMachinesByIds({
					machines: [machineID],
				});

				
				if (response1.status) {
					setProductName(response1.data[0].machine_name);
				} else {
					setProductName(""); // Revert to ID if failed
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
				setProductName("");
			}
		};

		fetchOrders();
		return () => {
			mounted = false;
			// Live-reproduced 2026-07-22: chip:navigate (the OTHER listener above) bumps
			// refreshTick, re-running this effect WHILE the fetch this same effect just started
			// is still in flight. That teardown sets `mounted = false` on THIS closure before its
			// response arrives, so its own `if (!mounted...) return` inside the try block skips
			// straight past `setOrders`/`setFilteredOrders` (correct — a superseded response
			// should be ignored) but ALSO skips the finally block's `if (mounted) setLoading(false)`
			// (NOT correct — nothing else was going to clear it). The NEW effect invocation's own
			// fetchOrders() call then hits the in-flight/same-requestKey dedup guard above (still
			// true, since finally's `ordersFetchInFlightRef.current = false` is itself gated on
			// `fetchSeq === ordersFetchSeqRef.current`, which no longer matches) and returns
			// immediately without ever calling setLoading at all. Net result: loading never
			// resolves back to false and the page is stuck on the skeleton forever — reproduced on
			// every CHIP-driven navigation to this page, not anything specific to a status filter.
			// Clearing the in-flight flag here, unconditionally, on ANY teardown (whether a real
			// unmount or just this effect being superseded) means the newer invocation's own
			// fetchOrders() call always passes the dedup guard cleanly and is the one responsible
			// for eventually calling setLoading(false) itself.
			ordersFetchInFlightRef.current = false;
		};
	}, [selectedSite, machineID, productID, hasMachineParam, hasProductParam, t, refreshTick]);

	useEffect(() => {
		if (!orderSchedulesEnabled) {
			setOrderSchedules([]);
			setFilteredSchedules([]);
			setSelectedScheduleViewer(null);
			setScheduleOpen(false);
			return;
		}
		if (!selectedSiteId) {
			setOrderSchedules([]);
			setFilteredSchedules([]);
			return;
		}
		fetchOrderSchedules();
	}, [orderSchedulesEnabled, selectedSiteId, fetchOrderSchedules]);

	useEffect(() => {
		if (!selectedOrder || !orderDetailsRef.current) return;

		setTimeout(() => {
			// Re-check inside the callback, not just before scheduling it — user-reported crash
			// 2026-07-22: "Cannot read properties of null (reading 'scrollIntoView')". The ref can
			// go null during this 150ms window (e.g. a near-simultaneous chip:navigate-driven
			// re-render tearing down/rebuilding the order-details panel, now firing more often
			// since CHIP-driven single-order deep links reliably resolve while already on this
			// page). The outer guard above only proves the ref was non-null at schedule time.
			if (!orderDetailsRef.current) return;
			orderDetailsRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 150);
	}, [selectedOrder]);


	const fetchTrackingDetailsForOrder = async (orderEntry) => {
		if (!orderEntry || orderEntry.source !== "past") return;

		const orderNumber = orderEntry.order_number || orderEntry.order_id;
		if (!orderNumber) return;
		if (trackingFetchedRef.current.has(orderNumber)) return;

		setTrackingLoadingOrder(orderNumber);
		setFullOrdersData((prev) =>
			(prev || []).map((entry) => {
				const entryKey = entry?.order?.order_number || entry?.order?.order_id;
				if (entryKey !== orderNumber) return entry;
				const updatedProducts = (entry.products || []).map((product) => ({
					...product,
					tracking_details: null,
				}));
				return { ...entry, products: updatedProducts };
			})
		);

		try {
			const response = await getOrderTrackingDetails({
				order_number: orderNumber,
				site_number: selectedSite,
			});

			if (!response?.status) {
				return;
			}

			const trackingList = Array.isArray(response.data) ? response.data : [];
			const trackingMap = new Map(
				trackingList.map((entry) => [
					String(entry?.item_number || ""),
					Array.isArray(entry?.tracking_details) ? entry.tracking_details : [],
				])
			);

			setFullOrdersData((prev) =>
				(prev || []).map((entry) => {
					const entryKey = entry?.order?.order_number || entry?.order?.order_id;
					if (entryKey !== orderNumber) return entry;

					const updatedProducts = (entry.products || []).map((product) => {
						const productNumber =
							product?.productsData?.product_number ||
							product?.product_number ||
							product?.productsData?.pnum ||
							"";
						const trackingDetails = trackingMap.get(String(productNumber)) || [];
						return { ...product, tracking_details: trackingDetails };
					});

					return { ...entry, products: updatedProducts };
				})
			);
		} catch (error) {
			console.error("Error fetching tracking details:", error);
		} finally {
			trackingFetchedRef.current.add(orderNumber);
			setTrackingLoadingOrder(null);
		}
	};

	// If a query param is present (open_id or open_number), auto-open details after orders load.
	// Strips the param from the address bar once used, same as Cases.js's ?open_case=/
	// WorkOrders.js's ?open_wo= handlers (user-requested consistency, 2026-07-22) — previously
	// this page left it in the URL indefinitely after opening the order.
	const openOrderStripPendingRef = useRef(false);
	const openOrderHandledForRef = useRef(null);
	useEffect(() => {
		if (openOrderStripPendingRef.current) {
			openOrderStripPendingRef.current = false;
			return;
		}
		try {
			const search = new URLSearchParams(location.search);
			const openId = search.get('open_id');
			const openNumber = search.get('open_number');
			const key = openId ? `id:${openId}` : openNumber ? `number:${openNumber}` : null;
			if (!key) return;
			if (openOrderHandledForRef.current === key) return;
			if (!(Array.isArray(orders) && orders.length > 0)) return;

			let match = null;
			if (openId) match = orders.find(o => String(o.order_id) === String(openId));
			if (!match && openNumber) match = orders.find(o => String(o.order_number) === String(openNumber));
			if (match) {
				openOrderHandledForRef.current = key;
				setSelectedOrder(match);
				fetchTrackingDetailsForOrder(match);

				search.delete('open_id');
				search.delete('open_number');
				const newSearch = search.toString();
				openOrderStripPendingRef.current = true;
				navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
			}
		} catch (_) { /* ignore */ }
	}, [location.search, orders]);

	// Dedicated "chip:navigate" handler for ?open_id=/?open_number= specifically (mirrors
	// Cases.js's onChipNavigateOpenCase / WorkOrders.js's onChipNavigateOpenWo). User-reported
	// 2026-07-22: asking CHIP to check a specific order while ALREADY on the Order History page
	// did not expand it — the location-based effect above only reliably resolves the query
	// param on a genuine location.search change, and React Router doesn't consistently
	// retrigger that when navigate() targets the SAME route the user is already on (the
	// identical "same-route no-op" gap the filter listener below already works around).
	// ChatBot.js dispatches this event synchronously with the real destination path BEFORE its
	// own deferred navigate() call, so detail.path's query string is authoritative here too,
	// independent of location-diffing timing/reliability.
	useEffect(() => {
		const onChipNavigateOpenOrder = (e) => {
			const dest = String(e?.detail?.path || "");
			if (!dest.startsWith("/order-history")) return;
			const qIndex = dest.indexOf("?");
			const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
			const search = new URLSearchParams(searchString);
			const openId = search.get('open_id');
			const openNumber = search.get('open_number');
			if (!openId && !openNumber) return;
			if (!(Array.isArray(orders) && orders.length > 0)) return;

			let match = null;
			if (openId) match = orders.find(o => String(o.order_id) === String(openId));
			if (!match && openNumber) match = orders.find(o => String(o.order_number) === String(openNumber));
			if (match) {
				setSelectedOrder(match);
				fetchTrackingDetailsForOrder(match);
			}
		};
		window.addEventListener("chip:navigate", onChipNavigateOpenOrder);
		return () => window.removeEventListener("chip:navigate", onChipNavigateOpenOrder);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orders]);

	// ?status=/?from=/?to= deep-link filters (CHIP's get_recent_orders via
	// buildOrderHistoryListPath, orders.tools.js) — mirrors Cases.js's/WorkOrders.js's
	// identical ?filter_*= handling: the chat answer was already correct ("Showing 5 of 12
	// invoiced orders"), but this page never read these params at all, so it kept showing
	// the full unfiltered list. Local copy of orders.tools.js's STATUS_ALIASES/
	// OPEN_STATUS_ALIASES/resolveStatusAliases — same "no shared module across repos,
	// duplicate the small alias map" convention already used elsewhere in this codebase
	// (e.g. analytics.tools.js's own local copy of these same aliases) — must stay in sync
	// with the backend's version if either changes.
	const ORDER_STATUS_ALIASES = {
		pending: ["pending"],
		confirmed: ["confirmed"],
		shipped: ["shipped"],
		delivered: ["delivered"],
		invoiced: ["invoiced"],
		canceled: ["canceled", "cancelled"],
	};
	const OPEN_ORDER_STATUS_ALIASES = ["pending", "confirmed", "shipped", "open order", "open"];
	const resolveOrderStatusAliases = (text) => {
		const raw = String(text || "").trim().toLowerCase();
		if (!raw) return null;
		for (const aliases of Object.values(ORDER_STATUS_ALIASES)) {
			if (aliases.some((a) => raw.includes(a))) return aliases;
		}
		if (/\bopen\b/.test(raw)) return OPEN_ORDER_STATUS_ALIASES;
		return [raw];
	};

	const filterStripPendingRef = useRef(false);
	const [chatOrderFilter, setChatOrderFilter] = useState(null);

	// Applies filters from a raw "?status=...&from=...&to=..." search string. Factored out
	// so it can ALSO be driven directly by the "chip:navigate" event below (same-route
	// re-ask, e.g. "show invoiced orders" -> "show open orders" without location.search
	// changing in a way React reliably re-fires effects for — see Cases.js/WorkOrders.js
	// for the live-reproduced bug this avoids). Always sets state from THIS request's
	// params (including clearing back to null when absent) so a new chat-driven request
	// fully REPLACES the previous filter instead of ANDing with a stale one.
	const applyOrderFiltersFromSearch = (searchString) => {
		const params = new URLSearchParams(searchString || "");
		const status = params.get("status");
		const from = params.get("from");
		const to = params.get("to");
		if (!status && !from && !to) {
			setChatOrderFilter(null);
			return false;
		}
		setChatOrderFilter({
			statusAliases: status ? resolveOrderStatusAliases(status) : null,
			from: from || null,
			to: to || null,
		});
		return true;
	};

	useEffect(() => {
		if (filterStripPendingRef.current) {
			filterStripPendingRef.current = false;
			return;
		}
		const hadAnyFilterParam = applyOrderFiltersFromSearch(location.search);
		if (hadAnyFilterParam) {
			const params = new URLSearchParams(location.search);
			["status", "from", "to"].forEach((k) => params.delete(k));
			const newSearch = params.toString();
			filterStripPendingRef.current = true;
			navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search, location.key]);

	useEffect(() => {
		const onChipNavigate = (e) => {
			const dest = String(e?.detail?.path || "");
			if (!dest.startsWith("/order-history")) return;
			const qIndex = dest.indexOf("?");
			const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
			applyOrderFiltersFromSearch(searchString);
		};
		window.addEventListener("chip:navigate", onChipNavigate);
		return () => window.removeEventListener("chip:navigate", onChipNavigate);
	}, []);

	// Date range has no column-filter equivalent on this grid (same limitation Cases.js/
	// WorkOrders.js already accept for their own date filters), so it's applied directly on
	// the data passed to the grid, same as those pages' dateRangeFilter/displayedCases.
	const displayedOrders = useMemo(() => {
		if (!chatOrderFilter?.from && !chatOrderFilter?.to) return filteredOrders;
		const fromDate = chatOrderFilter.from ? new Date(chatOrderFilter.from) : null;
		const toDate = chatOrderFilter.to ? new Date(chatOrderFilter.to) : null;
		if (toDate) toDate.setHours(23, 59, 59, 999);
		return (filteredOrders || []).filter((order) => {
			if (!order?.order_created_date) return false;
			const d = new Date(order.order_created_date);
			if (Number.isNaN(d.getTime())) return false;
			if (fromDate && d < fromDate) return false;
			if (toDate && d > toDate) return false;
			return true;
		});
	}, [filteredOrders, chatOrderFilter]);

	// Status, unlike date, DOES have a real column filter on this grid (order_status,
	// filterable: true below) — so it goes through ExpandableDataGrid's initialColumnFilters,
	// the SAME state its own filter-icon UI reads/writes, exactly like Cases.js's/
	// WorkOrders.js's own initialColumnFilters usage for their status/type/priority filters.
	// User-reported 2026-07-22: the earlier version filtered the data invisibly (a plain
	// useMemo, no UI representation at all) — the user could not SEE that a filter was active
	// via the grid's own filter icon, nor change/clear it the way they would any other column
	// filter. Cases.js/WorkOrders.js get this for free because their backend tools resolve a
	// status/type alias into the REAL exact column label value(s) server-side before it ever
	// reaches the frontend; orders.tools.js sends the raw typed word instead (`?status=invoiced`),
	// so resolveOrderStatusAliases above is still needed client-side — but its OUTPUT here is
	// the set of REAL order_status values actually present in this site's data that match the
	// alias, not a hidden filter predicate, so the grid treats it exactly like a value a user
	// picked by hand from the filter popover.
	const chatOrderStatusColumnFilter = useMemo(() => {
		if (!chatOrderFilter?.statusAliases) return null;
		const aliases = chatOrderFilter.statusAliases;
		const matchingValues = Array.from(new Set(
			(filteredOrders || [])
				.map((order) => String(order?.order_status || ""))
				.filter((status) => aliases.some((a) => status.toLowerCase().includes(a)))
		));
		return { order_status: matchingValues };
	}, [filteredOrders, chatOrderFilter]);

	useEffect(() => {

	}, [scheduleOpen]);

	// Utility function to flatten an object into a string for search
	const flattenObjectToString = (obj, extraTokens = []) => {
		let flatString = "";

		// Recursively traverse the object and build a flat string of values
		const flatten = (value) => {
			if (typeof value === "object" && value !== null) {
				Object.values(value).forEach(flatten);
			} else {
				flatString += ` ${value}`;
			}
		};

		flatten(obj);
		if (Array.isArray(extraTokens) && extraTokens.length > 0) {
			extraTokens.forEach((token) => {
				if (token == null) return;
				flatString += ` ${token}`;
			});
		}
		return flatString.toLowerCase(); // Convert to lowercase for case-insensitive search
	};

	const handleSearchChange = (value = "") => {
		const query = value?.toLowerCase() || "";
		setSearchQuery(query);

		const workingOrders = Array.isArray(orders) ? orders : [];
		if (!query) {
			setFilteredOrders(workingOrders);
			setFilteredSchedules(Array.isArray(orderSchedules) ? orderSchedules : []);
			return;
		}

		const filtered = workingOrders.filter((order) => flattenObjectToString(order).includes(query));

		setFilteredOrders(filtered);
		const schedulePool = Array.isArray(orderSchedules) ? orderSchedules : [];
		const filteredScheduleRows = schedulePool.filter((schedule) => {
			const statusLabel = schedule?.is_active ? "active" : "inactive";
			return flattenObjectToString(schedule, [statusLabel]).includes(query);
		});
		setFilteredSchedules(filteredScheduleRows);
		const resultCount = filtered.length + filteredScheduleRows.length;
		const trackingKey = `${query}|${selectedSite}|${resultCount}`;
		if (lastTrackedSearchRef.current !== trackingKey) {
			lastTrackedSearchRef.current = trackingKey;
			trackEvent("search_performed", {
				search_term: value,
				search_source: "buy_again_order_history",
				page_name: "Order History",
				page_path: `${location.pathname}${location.search || ""}`,
				result_count: resultCount,
				has_results: resultCount > 0,
				no_results: resultCount === 0,
			});
		}
	};

	useEffect(() => {
		setFilteredSchedules(Array.isArray(orderSchedules) ? orderSchedules : []);
	}, [orderSchedules]);
	const handleViewOrder = (order) => {
		setSelectedOrder(order); // Set the selected order to display its details
		fetchTrackingDetailsForOrder(order);
	};

	const handleViewSchedule = (schedule) => {
		setSelectedScheduleViewer(schedule); // Set the selected schedule to display its details
	};

	const handleCloseOrderDetails = () => {
		setSelectedOrder(null);
	};

	const exportOrdersToXLSX = (allOrders, selectedSite) => {
		const sheetData = [];
		const merges = [];

		// Define headers
		const headers = [
			t('ORDERS.ORDER_NUMBER'),
			t('ORDERS.PAYMENT_MODE'),
			t('ORDERS.PO_NUMBER'),
			t('ORDERS.USER'),
			t('ORDERS.ORDER_TOTAL'),
			t('ORDERS.CURRENCY'),
			t('ORDERS.STATUS'),
			t('ORDERS.CREATED_DATE'),
			t('ORDERS.UPDATED_DATE'),
			t('ORDERS.SITE_NUMBER'),
			t('ORDERS.ORDER_ID'),
			t('ORDERS.PRODUCT_NAME'),
			t('UI_TEXT.PRODUCT_PRICE'),
			t('ORDERS.QUANTITY'),
			t('ORDERS.SITE')
		];
		sheetData.push(headers);

		let rowIndex = 1; // Start from 1 due to header

		allOrders.forEach((order) => {
			const canShowOrderPrices = shouldShowPricesForOrder(order);
			const products = Array.isArray(order?.products) && order.products.length > 0
				? order.products
				: [null];

			products.forEach((product, idx) => {
				const row = [];

				if (idx === 0) {
					// First product row: include order-level info
					row.push(
						order.order_number,
						order.order_payment_mode,
						order.order_po,
						order.user_display_name,
						canShowOrderPrices ? order.order_total : t('UI_TEXT.NA_1'),
						canShowOrderPrices ? order.order_currency : t('UI_TEXT.NA_1'),
						order.order_status,
						formatDateTime(order.order_created_date),
						formatDateTime(order.order_update_date),
						order.site_number,
						order.order_id
					);
				} else {
					// Leave cells empty — will be merged
					for (let i = 0; i < 11; i++) row.push(null);
				}

				// Export the stored ordered line price first so history stays stable even if catalog prices change.
				let price = product?.unit_price_after_discount ?? product?.product_price;
				if (price === undefined || price === null || price === "") {
					switch (selectedSiteCurrency?.toLowerCase()) {
						case "cad":
							price = product?.productsData?.price_cad;
							break;
						case t('UI_TEXT.USD').toLowerCase():
							price = product?.productsData?.price_usd;
							break;
						case "eur":
							price = product?.productsData?.price_eur;
							break;
						default:
							price = 0;
					}
				}

				// Product-level data
				row.push(
					product?.productsData?.product_name || t('UI_TEXT.NA_1'),
					canShowOrderPrices ? (`${currencySymbol}${price}` || t('UI_TEXT.NA_1')) : t('UI_TEXT.NA_1'),
					product?.product_quantity ?? t('UI_TEXT.NA_1'),
					selectedSite
				);

				sheetData.push(row);
				rowIndex++;
			});

			if (products.length > 1) {
				// Merge the order-level cells for this order's rows
				for (let col = 0; col <= 10; col++) {
					merges.push({
						s: { r: rowIndex - products.length, c: col }, // start row/col
						e: { r: rowIndex - 1, c: col }, // end row/col
					});
				}
			}
		});

		const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
		worksheet["!merges"] = merges;

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, t('DATE_RANGES.ORDERS'));
		XLSX.writeFile(workbook, `OrdersExport_all_time.xlsx`);
	};

	const renderOrderDetails = () => {
		if (!selectedOrder) return null;
		if (!selectedOrder) return null;

		// Prefer the richer Orders-by-Site record when order_number matches
		let filteredOrder = fullOrdersData.find((o) => o?.order?.order_id === selectedOrder?.order_id);

		if (!filteredOrder) {
			const allMatches = fullOrdersData.filter((o) => o?.order?.order_number === selectedOrder?.order_number);
			if (allMatches.length) {
				// Heuristic: prefer a match that has PO filename or file URL (Orders by Site)
				filteredOrder =
					allMatches.find((o) => o?.details?.po_filename || o?.details?.po_file_url) ||
					allMatches[0];
			}
		}

		
		
		

		if (!filteredOrder) {
			Swal.fire({
				title: t('ORDERS.ORDER_NOT_FOUND'),
				text: t('ORDERS.DETAILS_FOR_THE_SELECTED_ORDER_ARE_NOT_FOUND'),
				icon: "info",
			});
			return null;
		}

		const { order, products } = filteredOrder;
		const isTrackingLoading =
			trackingLoadingOrder &&
			String(trackingLoadingOrder) === String(order?.order_number || order?.order_id);

		filteredOrder.products = Array.from(
			new Map(filteredOrder.products.map((product) => [product.product_id, product])).values()
		);

		return (
			<div ref={orderDetailsRef} style={{ scrollMarginTop: "120px" }}>
				<PageTitle title={`${t('TITLES.ORDER_DETAILS_1')}: ${order.order_number}`} />
				<div className="track-order-container">
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						{t('BUTTON.CLOSE')}
					</Button>
					{canShowBuyAgain && (
						<Button
							variant="contained"
							startIcon={<AddShoppingCartIcon />}
							color="primary"
							onClick={() => {
								handleOrderAgain(selectedOrder);
							}}>
							{t('BUTTON.BUY_AGAIN')}
						</Button>
					)}
				</div>
				<div className="order-tracker-container"></div>
				<OrderTracker orderData={filteredOrder} />
				<OrderDetails
					orderData={filteredOrder}
					trackingLoading={isTrackingLoading}
					showPrices={shouldShowPricesForOrder(selectedOrder)}
				/>

				<div className="all-orders-container">
					{canShowBuyAgain && (
						<Button
							variant="contained"
							startIcon={<AddShoppingCartIcon />}
							color="primary"
							onClick={() => {
								handleOrderAgain(selectedOrder);
							}}>
							{t('BUTTON.BUY_AGAIN')}
						</Button>
					)}
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						{t('BUTTON.CLOSE')}
					</Button>
				</div>
			</div>
		);
	};

	const renderScheduleDetails = () => {
		if (!selectedScheduleViewer) return null;

		const filteredOrder = orderSchedules.find(order => order?.id === selectedScheduleViewer?.id);

		// Defensive check for data structure
		if (!filteredOrder || !filteredOrder.checkoutdetails || !filteredOrder.checkoutdetails.orderdetails) {
			return (
				<div>
					<PageTitle title={t('TITLES.ORDER_DETAILS_NO_DATA')}
					/>
					<div className="all-orders-container">
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setSelectedScheduleViewer(null)}
						>
							{t('BUTTON.CLOSE')}
						</Button>
					</div>
				</div>
			);
		}

		// Safe product processing
		const products = filteredOrder.checkoutdetails.orderdetails.products || [];
		filteredOrder.products = Array.from(
			new Map(products.map(product => [product.productid, product])).values()
		);

		return (
			<div>
				<PageTitle title={`${t('TITLES.ORDER_DETAILS_1')} ${filteredOrder?.schedulenumber || 'N/A'}`} />
				<OrderDetails orderData={filteredOrder} showPrices={canShowOrderPrices} />
				<div className="all-orders-container">
					{canShowBuyAgain && (
						<Button
							variant="contained"
							startIcon={<AddShoppingCartIcon />}
							color="primary"
							onClick={() => handleOrderAgain(selectedOrder)}
						>
							{t('BUTTON.BUY_AGAIN')}
						</Button>
					)}
					<Button
						variant="outlined"
						color="primary"
						onClick={() => setSelectedScheduleViewer(null)}
					>
						{t('BUTTON.CLOSE')}
					</Button>
				</div>
			</div>
		);
	};


	const handleOrderAgain = (row) => {
		if (!canShowBuyAgain) return;
		const summary = buildBuyAgainSummary(row);
		setBuyAgainSummary(summary);
		setBuyAgainOpen(true);
	};

	const renderOrderMasterDetails = (orderRow) => {
		const rowOrderNumber = orderRow?.order_number || orderRow?.order_id;
		const isRowTrackingLoading =
			trackingLoadingOrder &&
			String(trackingLoadingOrder) === String(rowOrderNumber);
		return (
			<Box sx={{
				p: 2, width: '100%', border: "1px solid #e6e8eb",
				borderRadius: 2,
			}}>
				<Box sx={{ display: "flex", justifyContent: "space-between", my: 1 }}>
					<Box>
						<Typography variant="body1" fontWeight={"bold"}>Order Details: {rowOrderNumber}</Typography>
						<Typography variant="body2" fontSize={"11px"} color="text.secondary">View the list of parts ordered</Typography>
					</Box>
					{canShowBuyAgain && (
						<Button
							variant="contained"
							startIcon={<AddShoppingCartIcon />}
							color="primary"
							size="small"
							onClick={() => handleOrderAgain(orderRow)}
						>
							{t('BUTTON.BUY_AGAIN')}
						</Button>
					)}
				</Box>
				<OrderDetails
					orderData={{
						order: orderRow || {},
						details: {},
						products: Array.isArray(orderRow?.products) ? orderRow.products : [],
					}}
					partDetailsOnly={true}
					trackingLoading={isRowTrackingLoading}
					showPrices={shouldShowPricesForOrder(orderRow)}
				/>

			</Box>
		);
	};

	const addPurchasableToCart = async (items = []) => {
		if (!canShowBuyAgainCart) return [];
		const cartAdd = items.map((item) => ({
			ProductID: item.product_id,
			Quantity: item.quantity || 1,
			Site: selectedSite,
		}));
		if (cartAdd.length > 0) {
			await addMultipleToCart(cartAdd);
		}
		return cartAdd;
	};

	const addNonPurchasableToRequestList = async (items = []) => {
		if (!canShowBuyAgainRequestList) return { added: 0, skipped: items.length };
		const results = { added: 0, skipped: 0 };
		for (const item of items) {
			if (!item.machine_id || !item.product_id) {
				results.skipped += 1;
				continue;
			}
			try {
				const req_body = {
					machine_id: item.machine_id,
					product_id: item.product_id,
					site_number: selectedSite,
					user_id: user?.user_id,
				};
				const response = await saveRequestList(req_body);
				if (response?.status) results.added += 1;
				else results.skipped += 1;
			} catch {
				results.skipped += 1;
			}
		}
		return results;
	};

	/** DataGrid columns for the buy-again modal; the part-number cell links a replacement to its detail page. */
	const buyAgainColumns = [
		{
			field: "product_number",
			headerName: t("LABELS.PART_NUMBER") || t('UI_TEXT.PART'),
			flex: 1,
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
					<span>{params.value}</span>
					<ReplacementPartNotice product={params.row} linkToReplacement />
				</Box>
			),
		},
		{ field: "product_name", headerName: t("TABLE_HEADERS.PART_NAME") || t("TABLE_HEADERS.PART_NAME"), flex: 2 },
		{ field: "quantity", headerName: t("UI_TEXT.QTY") || t("UI_TEXT.QTY"), width: 90 },
		{
			field: "confirmed_shipping_date",
			headerName: t("TABLE_HEADERS.CONFIRM_SHIP_DATE", { defaultValue: "Confirm Ship Date" }),
			flex: 1,
			valueFormatter: (value) => formatDate(value),
		},
		{ field: "machine_number", headerName: t("TITLES.MACHINE_NUMBER") || t("TITLES.MACHINE_NUMBER"), flex: 1 }
	];

	const handleBuyAgainAction = async (mode) => {
		const { purchasable, nonPurchasable, notAvailable, orderNumber } = buyAgainSummary;
		const hasPurchasable = purchasable.length > 0;
		const hasNonPurchasable = nonPurchasable.length > 0;
		const allowCart = canShowBuyAgainCart && (mode === "cart" || mode === "cart_and_request");
		const allowRequest = canShowBuyAgainRequestList && (mode === "request" || mode === "cart_and_request");

		try {
			setBuyAgainBusy(true);
			let cartAdded = 0;
			let cartQty = 0;
			let requestAdded = 0;
			let requestSkipped = 0;

			if (allowCart) {
				const cartAdd = await addPurchasableToCart(purchasable);
				cartAdded = cartAdd.length;
				cartQty = cartAdd.reduce((sum, i) => sum + Number(i.Quantity || 0), 0);
			}

			if (allowRequest) {
				const req = await addNonPurchasableToRequestList(nonPurchasable);
				requestAdded = req.added;
				requestSkipped = req.skipped;
			}

			const lines = [
				hasPurchasable ? `${t('ORDERS.PURCHASABLE_PARTS_ADDED_TO_CART')}: <b>${cartAdded}</b> (${t('ORDERS.QTY')} ${cartQty})` : t('ORDERS.NO_PURCHASABLE_PARTS_TO_ADD_TO_CART'),
				hasNonPurchasable ? `${t('ORDERS.NON_PURCHASABLE_PARTS_ADDED_TO_REQUEST_LIST')}: <b>${requestAdded}</b>` : t('ORDERS.NO_NON_PURCHASABLE_PARTS_TO_ADD_TO_REQUEST_LIST'),
				requestSkipped ? `${t('ORDERS.SKIPPED_REQUEST_LIST_ITEMS')}: <b>${requestSkipped}</b>` : "",
				notAvailable.length ? `${t('ORDERS.NOT_AVAILABLE_PARTS')}: <b>${notAvailable.length}</b>` : "",
			].filter(Boolean);

			Swal.fire({
				icon: "success",
				title: `${t('BUTTON.BUY_AGAIN')} Summary${orderNumber ? ` - ${orderNumber}` : ""}`,
				html: lines.join("<br/>"),
			});
			setBuyAgainOpen(false);
		} catch (e) {
			Swal.fire({ icon: "error", title: "Error", text: "Failed to add parts from this order." });
		} finally {
			setBuyAgainBusy(false);
		}
	};

	const buildBuyAgainPrefill = () => {
		const allParts = [
			...buyAgainSummary.purchasable,
			...buyAgainSummary.nonPurchasable,
			...buyAgainSummary.notAvailable,
		].map((part) =>
			getEffectiveProductDisplay(part, {
				machineId: part.machine_id,
				currency: selectedSiteCurrency,
			})
		);
		const uniqueMachines = Array.from(
			new Set(allParts.map((p) => p.machine_number).filter(Boolean))
		);
		const partsText = allParts.length
			? allParts.map((p) => `${p.product_number || "N/A"} - ${p.product_name || "Part"}`).join(", ")
			: "the parts from my order";

		const orderLabel = buyAgainSummary.orderNumber ? `Order ${buyAgainSummary.orderNumber}` : "my order";

		return {
			subject: `Need help purchasing parts from ${orderLabel}`,
			machine_number: uniqueMachines.join(", "),
			message: `Hello CCR team, I would like to purchase the following parts from ${orderLabel}: ${partsText}. Please help me with availability and next steps.`,
		};
	};

	const handleContactCCRFromBuyAgain = () => {
		setCcrPrefill(buildBuyAgainPrefill());
		setCcrOpen(true);
		setBuyAgainOpen(false);
	};

	const resolvePoFileUrl = (row = {}) => {
		const rawUrl = String(row?.po_file_url || "").trim();
		if (!rawUrl) return "";
		if (/^(https?:)?\/\//i.test(rawUrl)) return rawUrl;
		const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
		return `${ASSETS_BASE_URL}${normalizedPath}`;
	};
	// Define columns for the Datagrid
	let columns = [
		{
			field: "order_number",
			headerName: t('TABLE_HEADERS.ORDER_NUMBER'),
			flex: 3,
			renderCell: (params) => (
				<Tooltip title={t('TITLES.VIEW_ORDER_TRACKING_INFORMATION')}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}>
						{params.value}
					</span>
				</Tooltip>
			),
		},
		{ field: "order_payment_mode", flex: 1.5, headerName: t('TABLE_HEADERS.PAYMENT_MODE') },
		{
			field: "order_po",
			headerName: t('TABLE_HEADERS.PO'),
			flex: 1.5,
			renderCell: (params) => {
				const fileUrl = resolvePoFileUrl(params.row);
				return (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
						<Box
							component="span"
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								minWidth: 0,
							}}
						>
							{params.value || "N/A"}
						</Box>
						{fileUrl ? (
							<Tooltip title={t("TITLES.VIEW_ATTACHMENT", { defaultValue: "View attachment" })}>
								<IconButton
									size="small"
									color="primary"
									onClick={(event) => {
										event.stopPropagation();
										window.open(fileUrl, "_blank", "noopener,noreferrer");
									}}
								>
									<AttachmentIcon />
								</IconButton>
							</Tooltip>
						) : null}
					</Box>
				);
			},
		},
		{
			field: "user_display_name",
			headerName: "Ordered By",
			filterable: true,
			flex: 1,
			renderCell: (params) =>
				params?.value === "Deleted User" ? (
					<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>
				) : params?.value === "" ? (<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>) : (
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", height: "100%", width: "100%" }}>
						<UserName name={params.row.user_display_name || `${params.row.user_first_name || ''} ${params.row.user_last_name || ''}`.trim()} />
					</Box>
				),
		},
		{ field: "order_status", filterable: true, headerName: t('TABLE_HEADERS.STATUS'), width: 120 },
		{
			field: "order_created_date",
			headerName: "Date",
			valueFormatter: (value) => formatDate(value),
			// Ensure proper date sorting even if value is a string
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
			flex: 1,

		},
	];
	if (showOrderPricesForAll) {
		columns.splice(4, 0,
			{
				field: "order_total",
				headerName: t('TABLE_HEADERS.TOTAL'),
				width: 120,
				headerAlign: "center",
				align: "center",
				renderCell: (params) => {
					if (!shouldShowPricesForOrder(params.row)) return "N/A";
					const value = params.value;
					if (value == null || isNaN(Number(value))) return formatCurrency(0, selectedSiteCurrency);
					return formatCurrency(Number(value), selectedSiteCurrency);
				},
			},
			{
				field: "order_currency",
				headerName: "Curreny",
				filterable: true,
				flex: 1,
				renderCell: (params) => (shouldShowPricesForOrder(params.row) ? String(params.value || selectedSiteCurrency || "USD").toUpperCase() : "N/A"),
			}
		);
	}

	// 👉 Add action column only if at least one permission is true
	if (canSeeOrderHistory || canCreateOrderSchedules) {
		columns.push({
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 120,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => {
				const orderProducts = params.row?.products || [];
				const hasPurchasable = orderProducts.some((product) =>
					isPurchasableProduct(product?.productsData ?? product)
				);
				return (
					<div>
						{canSeeOrderHistory && canShowBuyAgain && (
							<Tooltip title={t('TABLE_HEADERS.ORDER_AGAIN')}>
								<IconButton
									color="primary"
									aria-label="Order Again"
									onClick={(event) => {
										event.stopPropagation();
										handleOrderAgain(params.row);
									}}>
									<AddShoppingCartIcon />
								</IconButton>
							</Tooltip>
						)}
						{canCreateOrderSchedules && hasPurchasable && (
							<Tooltip title={t('TITLES.ADD_ORDER_TO_SCHEDULE')}>
								<IconButton
									color="primary"
									aria-label="Schedule Order"
									onClick={(event) => {
										event.stopPropagation();
										handleScheduleOrder(params.row);
									}}>
									<ScheduleIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
				);
			},
		});
	}


	let columns_schedules = [
		{
			field: "schedule_number",
			headerName: t('TABLE_HEADERS.SCHEDULER_NUMBER'),
			flex: 1.75,
			renderCell: (params) => (
				<Tooltip title={t('TITLES.VIEW_ORDER_SCHEDULE_DETAILS')}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}>
						{params.value}
					</span>
				</Tooltip>
			),
		},

		{
			field: "frequency",
			headerName: t('LABELS.FREQUENCY'),
			flex: 1,
			valueFormatter: (params) => {
				const value = params;
				return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
			},
		},
		{
			field: "start_date",
			headerName: t('TABLE_HEADERS.START_DATE'),
			flex: 1,
			valueFormatter: (value) => formatDateTime(value),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
			width: 150,
		},
		{
			field: "end_date",
			headerName: t('TABLE_HEADERS.END_DATE'),
			flex: 1,
			valueFormatter: (value) => formatDateTime(value),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
			width: 150,
		},
		{
			field: "nextInvocation",
			headerName: t('TABLE_HEADERS.NEXT_INVOCATION'),
			flex: 1,
			valueFormatter: (value) => (value ? formatDateTime(value) : t('UI_TEXT.NA_1')),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
			width: 150,
		},
		{
			field: "user_display_name",
			headerName: t('TABLE_HEADERS.CREATED_BY'),
			flex: 1,
			renderCell: (params) =>
				params?.value === "Deleted User" ? (
					<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>
				) : params?.value === "" ? (<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>) : (
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", height: "100%", width: "100%" }}>
						<UserName name={params.row.user_display_name || `${params.row.user_first_name || ''} ${params.row.user_last_name || ''}`.trim()} />
					</Box>
				),
		},
		{
			field: "created",
			headerName: t('TABLE_HEADERS.CREATED_DATE'),
			flex: 1,
			valueFormatter: (value) => formatDateTime(value),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
			width: 150,
		},
		{
			field: "is_active",
			headerName: t('TABLE_HEADERS.STATUS'),
			headerAlign: 'center',
			align: 'center',
			flex: 1,
			renderCell: (params) => {
				const isActive = params.row.is_active;
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
						<CustomChip text={isActive ? t("MENU_ITEMS.ACTIVE") : t('MENU_ITEMS.INACTIVE')} color={isActive ? 'success' : 'warning'} title={'Status'} />
					</Box>

				);
			},
		},
	];

	// 👉 Add the ACTIONS column only if user can either edit or delete schedules
	if (canEditOrderSchedules || canDeleteOrderSchedules) {
		columns_schedules.push({
			field: "schedule_actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 160,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<div>
					<Tooltip title={params.row.is_active ? "Mark inactive" : "Mark active"}>
						<span>
							<Switch
								size="small"
								checked={!!params.row.is_active}
								disabled={!canEditOrderSchedules}
								onClick={(event) => event.stopPropagation()}
								onChange={(event) => handleScheduleStatusToggle(event, params.row)}
							/>
						</span>
					</Tooltip>
					{/* Show Edit only if allowed */}
					{canEditOrderSchedules && (
						<Tooltip title={t('TITLES.EDIT_SCHEDULE')}>
							<IconButton
								color="primary"
								aria-label="Edit Schedule"
								onClick={() => handleScheduleEdit(params.row)}>
								<EditIcon />
							</IconButton>
						</Tooltip>
					)}

					{/* Show Delete only if allowed */}
					{canDeleteOrderSchedules && (
						<Tooltip title={t('TITLES.DELETE_SCHEDULE')}>
							<IconButton
								color="error"
								aria-label="Delete Schedule"
								onClick={() => handleScheduleDelete(params.row)}>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</div>
			),
		});
	}

	const handleSchedulesSelection = (selectedRows) => {
		setSelectedScheduleRows(selectedRows);
	};

	const handleRowClick = (params) => {
		handleViewOrder(params.row);
	};

	const handleScheduleClick = (params) => {
		// Validate that the schedule has the required data structure
		if (params.row) {
			setSelectedScheduleViewer(params.row);
		} else {
			console.warn("Selected schedule missing required data structure:", params.row);
			triggerToast("Unable to view schedule details - invalid data structure", "error");
		}
	};

	const handleScheduleOrder = (row = undefined) => {
		setScheduleOpen(true);
		setSelectedOrderSchedule(row);
	};

	const handleScheduleEdit = (row) => {
		setScheduleOpen(true);
		setSelectedOrderSchedule({
			...row,
			products: row.checkout_details?.order_details?.products
		});
	};

	const handleScheduleStatusToggle = async (event, row) => {
		event.stopPropagation();
		const prevActive = row.is_active;
		const nextActive = !prevActive;

		setOrderSchedules((prev) =>
			prev.map((schedule) =>
				schedule.id === row.id ? { ...schedule, is_active: nextActive } : schedule
			)
		);

		if (selectedScheduleViewer?.id === row.id) {
			setSelectedScheduleViewer((prev) =>
				prev ? { ...prev, is_active: nextActive } : prev
			);
		}

		try {
			const response = await scheduleUpdate({
				id: row.id,
				job_id: row.job_id,
				is_active: nextActive,
				frequency: row.frequency,
				start_date: row.start_date,
				end_date: row.end_date,
				checkout_details: row.checkout_details,
				origin: row.origin,
				origin_id: row.origin_id,
				user_id: row.user_id,
				site_id: row.site_id,
				custom_number: row.custom_number,
				custom_unit: row.custom_unit,
			});

			if (!response?.status) {
				throw new Error("Update failed");
			}

			triggerToast(
				`Schedule ${nextActive ? "activated" : "inactivated"} successfully!`,
				"success"
			);
		} catch (error) {
			setOrderSchedules((prev) =>
				prev.map((schedule) =>
					schedule.id === row.id ? { ...schedule, is_active: prevActive } : schedule
				)
			);
			if (selectedScheduleViewer?.id === row.id) {
				setSelectedScheduleViewer((prev) =>
					prev ? { ...prev, is_active: prevActive } : prev
				);
			}
			triggerToast("Failed to update schedule status. Please try again.", "error");
		}
	};
	useEffect(() => {
		// Clear selected schedule viewer if it no longer exists in the schedules list
		if (selectedScheduleViewer && orderSchedules.length > 0) {
			const exists = orderSchedules.find(schedule => schedule.id === selectedScheduleViewer.id);
			if (!exists) {
				setSelectedScheduleViewer(null);
			}
		}
	}, [orderSchedules, selectedScheduleViewer]);

	const handleScheduleDelete = async (row) => {
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: t('ALERT_MESSAGES.THIS_WILL_DELETE_SCHEDULE_PERMANENTLY'),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText: t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;

			const deleteRow = await deleteOrderSchedule(row.id);
			
			if (deleteRow.status) {
				// Clear selected schedule viewer if it's the deleted one
				if (selectedScheduleViewer && selectedScheduleViewer.id === row.id) {
					setSelectedScheduleViewer(null);
				}

				// Update local state immediately to prevent UI errors
				setOrderSchedules(prevSchedules =>
					prevSchedules.filter(schedule => schedule.id !== row.id)
				);

				triggerToast(`Schedule deleted successfully!`, "success");

				// Fetch updated schedules from backend as secondary confirmation
				const fetchOrderSchedules = async () => {
					try {
						const response = await getOrderSchedules(selectedSiteData.site_id);
						
						if (response.status) {
							setOrderSchedules(response.data);
						} else {
							setOrderSchedules([]);
						}
					} catch (error) {
						console.error("Error while fetching order schedules", error);
						// Don't set empty array here as deletion was successful
						// Keep the current filtered state
					}
				};
				fetchOrderSchedules();

			} else {
				triggerToast(`Error Deleting Schedule. Please Try Again!`, "error");
			}

		} catch (error) {
			console.error("Error during deletion:", error);

			// Check if it's a successful deletion with response error
			if (error.response && error.response.data && error.response.status) {
				// Backend deletion was successful despite the error
				if (selectedScheduleViewer && selectedScheduleViewer.id === row.id) {
					setSelectedScheduleViewer(null);
				}

				setOrderSchedules(prevSchedules =>
					prevSchedules.filter(schedule => schedule.id !== row.id)
				);

				triggerToast(`Schedule deleted successfully!`, "success");

				// Refresh the schedules list
				const fetchOrderSchedules = async () => {
					try {
						const response = await getOrderSchedules(selectedSiteData.site_id);
						
						if (response.status) {
							setOrderSchedules(response.data);
						}
					} catch (fetchError) {
						console.error("Error while fetching order schedules after successful delete", fetchError);
					}
				};
				fetchOrderSchedules();
			} else {
				// Actual error occurred
				triggerToast(`Error Deleting Schedule. Please Try Again!`, "error");
			}
		}
	}

	// Build visible tabs list dynamically
	const visibleTabs = [];
	if (canSeeOrderHistory) visibleTabs.push({ label: t('LABELS.ORDER_HISTORYS'), key: 'history' });
	if (orderSchedulesEnabled && canSeeOrderSchedules) visibleTabs.push({ label: t('LABELS.ORDER_SCHEDULES'), key: 'schedules' });

	useEffect(() => {
		// Update authorization
		setIsAuthorized(canSeeOrderHistory || (orderSchedulesEnabled && canSeeOrderSchedules));

		// Reset tab to 0 if permissions changed or invalid
		if (tab >= visibleTabs.length && visibleTabs.length > 0) {
			setTab(0);
		}
	}, [canSeeOrderHistory, canSeeOrderSchedules, visibleTabs.length, orderSchedulesEnabled, tab]);

	const handleTabChange = (event, newValue) => {
		setSelectedOrder(null);
		setTab(newValue);
	};

	if (!isAuthorized) {
		return <NotAuthorized />;
	}

	if (loading) {
		return (
			<DataGridSkeleton
				containerClassName="order-history-container"
				showTabs={visibleTabs.length > 0}
				tabCount={Math.max(visibleTabs.length, 1)}
				actionButtons={1}
				columns={5}
				rows={6}
			/>
		);
	}


	return (
		<div className="order-history-container" data-tour="oh-main">
			<PageTitle title={t('DATE_RANGES.ORDERS')} subtitle={productName !== "" && `Orders for ` + productName} />
			{visibleTabs.length > 1 && (
				<Tabs
					value={tab}
					onChange={handleTabChange}
					aria-label="orders tabs"
					className="profile-tabs"
					data-tour="oh-tab"
				>
					{visibleTabs.map((tItem, index) => (
						<Tab key={tItem.key} label={tItem.label} />
					))}
				</Tabs>
			)}


			{visibleTabs[tab]?.key === 'history' && canSeeOrderHistory && (
				<>

					<div
						className="orders-serach-container"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Box sx={{ width: { xs: "100%", md: "20%" } }}>
							<ContextFilterAndSort
								searchValue={searchQuery}
								onSearchChange={handleSearchChange}
								placeholder={t('PLACEHOLDER.SEARCH_ORDERS')}
								showSearch={true}
								showSort={false}
							/>
						</Box>
						<Box
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								flexDirection: "row",
								columnGap: "10px",
							}}
						>
							<Typography
								variant="body1"
								fontSize="11px"
								sx={{ color: "#aaa", mt: 2, fontWeight: "bold" }}
							>
								{t('UI_TEXT.SHOWING_ALL_ORDERS_TO_DATE')}
							</Typography>
							<Button
								variant="contained"
								color="primary"
								size="small"
								startIcon={<FileDownloadIcon />}
								onClick={() => exportOrdersToXLSX(filteredOrders, selectedSite)}
							>
								{t('BUTTON.EXPORT_ORDERS')}
							</Button>
						</Box>
					</div>

					<ExpandableDataGrid
						key="orderHistoryGrid"
						data={displayedOrders}
						columns={columns}
						rowClick={true}
						onRowClick={handleRowClick}
						pageSize={5}
						showSearchBar={false}
						checkboxSelection={false}
						rowIdField="order_id"
						sortBy={[{ field: "order_created_date", sort: "desc" }]}
						masterDetails={renderOrderMasterDetails}
						allowMultipleExpand={false}
						initialColumnFilters={chatOrderStatusColumnFilter}
					/>

					<Typography
						variant="body1"
						fontSize="11px"
						sx={{ color: "#aaa" }}
					>
						<b>{t('UI_TEXT.NOTE')}</b> {t('UI_TEXT.CLICK_ORDER_NUMBER_TO_VIEW_DETAILS')}
					</Typography>

					{renderOrderDetails()}

				</>
			)}

			{/*   Order Schedules Tab */}

			{visibleTabs[tab]?.key === 'schedules' && orderSchedulesEnabled && canSeeOrderSchedules && (
				<>
					{orderSchedules?.length <= 0 ? (<>
						<Alert severity="info" className="error-alert">
							{t('ORDERS.NO_ORDER_SCHEDULES_FOUND_FOR_CUSTOMER_LOCATION')}
						</Alert>
						{canCreateOrderSchedules && (
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => handleScheduleOrder()}
									startIcon={<AddCircleOutlineIcon />}
								>
									{t('BUTTON.CREATE_ORDER_SCHEDULES')}
								</Button>
							</Box>
						)}
					</>) : (<>
						<div
							className="orders-serach-container"
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Box sx={{ width: { xs: "100%", md: "20%" } }}>
								<ContextFilterAndSort
									searchValue={searchQuery}
									onSearchChange={handleSearchChange}
									placeholder={t('PLACEHOLDER.SEARCH_ORDER_SCHEDULES')}
									showSearch={true}
									showSort={false}
								/>
							</Box>
							{canCreateOrderSchedules && (
								<Box
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "flex-end",
										flexDirection: "row",
										columnGap: "10px",
									}}
								>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={() => handleScheduleOrder()}
										startIcon={<AddCircleOutlineIcon />}
									>
										{t('BUTTON.CREATE_ORDER_SCHEDULES')}
									</Button>
								</Box>
							)}
						</div>

						<Datagrid
							key="orderscheduleGrid"
							data={filteredSchedules}
							columns={columns_schedules}
							rowClick={true}
							onRowClick={handleScheduleClick}
							pageSize={5}
							showSearchBar={false}
							checkboxSelection={false}
							onSelect={handleSchedulesSelection}
							rowIdField="job_id"
							sortBy={[{ field: "created", sort: "desc" }]}
						/>
					</>)}
				</>
			)}
			<ModalWrapper
				open={buyAgainOpen}
				setOpen={setBuyAgainOpen}
				width="70vw"
				title={`${t('BUTTON.BUY_AGAIN')}${buyAgainSummary.orderNumber ? ` - Order ${buyAgainSummary.orderNumber}` : ""}`}
				subtitle={t('ORDERS.REVIEW_PARTS_BEFORE_ADDING')}
				content={
					<Box sx={{ width: "100%" }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic", fontSize: "11px" }}>
							{t('ORDERS.PURCHASABLE_PARTS_INFO')}
						</Typography>

						{buyAgainSummary.purchasable.length === 0 && buyAgainSummary.nonPurchasable.length === 0 && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								Parts are not available to purchase. Please contact CCR for further details.
							</Alert>
						)}

						{buyAgainSummary.purchasable.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom fontWeight={600}>
									{t('UI_TEXT.PURCHASABLE_PARTS')} ({buyAgainSummary.purchasable.length})
								</Typography>
								<Datagrid
									data={buyAgainSummary.purchasable}
									columns={buyAgainColumns}
									rowIdField="id"
									showSearchBar={false}
								/>
							</Box>
						)}

						{buyAgainSummary.nonPurchasable.length > 0 && (
							<>
								<Divider sx={{ my: 2 }} />
								<Typography variant="h6" gutterBottom fontWeight={600}>
									{t('UI_TEXT.NON_PURCHASABLE_PARTS')} ({buyAgainSummary.nonPurchasable.length})
								</Typography>
								<Datagrid
									data={buyAgainSummary.nonPurchasable}
									columns={buyAgainColumns}
									rowIdField="id"
									showSearchBar={false}
								/>
							</>
						)}

						{buyAgainSummary.notAvailable.length > 0 && (
							<>
								<Divider sx={{ my: 2 }} />
								<Typography variant="h6" gutterBottom fontWeight={600}>
									Not Available Parts ({buyAgainSummary.notAvailable.length})
								</Typography>
								<Datagrid
									data={buyAgainSummary.notAvailable}
									columns={buyAgainColumns}
									rowIdField="id"
									showSearchBar={false}
								/>
							</>
						)}
					</Box>
				}
				actions={
					<>
						<Button variant="outlined" onClick={() => setBuyAgainOpen(false)} disabled={buyAgainBusy}>
							{t("BUTTON.CLOSE")}
						</Button>
						{canShowBuyAgainCart && buyAgainSummary.purchasable.length > 0 && buyAgainSummary.nonPurchasable.length === 0 && (
							<Button variant="contained" onClick={() => handleBuyAgainAction("cart")} disabled={buyAgainBusy}>
								{t('BUTTONS.ADD_TO_CART')}
							</Button>
						)}
						{buyAgainSummary.purchasable.length > 0 && buyAgainSummary.nonPurchasable.length > 0 && (
							<>
								{canShowBuyAgainCart && (
									<Button variant="contained" onClick={() => handleBuyAgainAction("cart")} disabled={buyAgainBusy}>
										{t('BUTTONS.ADD_TO_CART')}
									</Button>
								)}
								{canShowBuyAgainCart && canShowBuyAgainRequestList && (
									<Button variant="contained" onClick={() => handleBuyAgainAction("cart_and_request")} disabled={buyAgainBusy}>
										{t('BUTTONS.ADD_TO_CART_AND_REQUEST_LIST')}
									</Button>
								)}
								{canShowBuyAgainRequestList && (
									<Button variant="contained" onClick={() => handleBuyAgainAction("request")} disabled={buyAgainBusy}>
										{t('BUTTONS.ADD_TO_REQUEST_LIST')}
									</Button>
								)}
							</>
						)}
						{canShowBuyAgainRequestList && buyAgainSummary.purchasable.length === 0 && buyAgainSummary.nonPurchasable.length > 0 && (
							<Button variant="contained" onClick={() => handleBuyAgainAction("request")} disabled={buyAgainBusy}>
								{t('BUTTONS.ADD_TO_REQUEST_LIST')}
							</Button>
						)}
						{buyAgainSummary.purchasable.length === 0 && buyAgainSummary.nonPurchasable.length === 0 && (
							<Button variant="contained" onClick={handleContactCCRFromBuyAgain} disabled={buyAgainBusy}>
								{t('LABELS.CONTACT_CCR')}
							</Button>
						)}
					</>
				}
			/>
			<ContactCCRForm open={ccrOpen} setOpen={setCcrOpen} prefill={ccrPrefill} />
			{orderSchedulesEnabled && (
				<ScheduleOrder
					open={scheduleOpen}
					setOpen={setScheduleOpen}
					selectedOrder={selectedOrderSchedule}
					source={selectedOrder ? "orderHistory" : "createSchedule"}
					onScheduleSaved={fetchOrderSchedules}
				/>
			)}
		</div>
	);
};

export default OrderHistory;
